import { useEffect, useState } from 'react';
import { setUser as SentryUser } from '@sentry/react';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import fetchFeatureFlagsRequest from 'APIs/flagrAPI/evaluationBatch';
import { useFetchNotifications } from 'APIs/letrusAPI/notifications';
import { fetchUserDataRequest } from 'APIs/letrusAPI/user';
import { User, UserRole } from 'APIs/letrusAPI/user/types';
import Loading from 'components/Loading';
import UnderMaintenance from 'containers/UnderMaintenance';
import AuthLayout from 'layouts/AuthLayout';
import {
  ensureAmplitudeUser,
  initAmplitude,
  isAmplitudeReady,
} from 'utils/analytics/setupAmplitude';
import getFeatureFlag, {
  findFeatureFlag,
  hasStoredFeatureFlags,
} from 'utils/functions/getFeatureFlags';
import { getUserData } from 'utils/functions/getUserData';
import { useCrossDomainLogout } from 'utils/hooks/useCrossDomainLogout';
import setupUserHubspotConversation from 'utils/hubspot/setupUserHubspotConversation';
import LogoutModal from '../LogoutModal/LogoutModal';

const flagKeys = [
  'homepage_alert',
  'create_letters',
  'create_student',
  'check_ai_portal',
  'teacher_library',
  'evolution_portal',
  'maintenance_mode',
  'teacher_engagement',
  'tx_original_portal',
  'teacher_c5_dashboard',
  'teacher_recommendations_alerts',
  'overloaded_review_queue_warning',
  'show_worst_competence_mediation',
  'teacher_socioemotional_dashboard',
  'max_grade_justification',
  'enable_modal_perfil_professor',
  'enable_new_home',
  'evolution_alert',
  'unplugged_AI',
];

interface UserSession {
  user?: User;
  userRoleList?: UserRole[];
}

function AuthRouteMiddleware() {
  const [isCheckingAuth, setIsCheckingAuth] = useState(true);
  const [isUnderMaintenance, setIsUnderMaintenance] = useState(false);

  useCrossDomainLogout();
  const navigate = useNavigate();
  const { pathname } = useLocation();

  const fetchUserData = fetchUserDataRequest();
  const fetchFeatureFlags = fetchFeatureFlagsRequest();
  const hasLocalFeatureFlags = hasStoredFeatureFlags();

  const notifications = useFetchNotifications();
  const notificationsData = notifications?.data?.results || [];
  const unseenNotificationCount = notificationsData.filter(
    (notification) =>
      !notification.received &&
      notification.channel !== ('recommendation' as string)
  ).length;

  const localUserData = getUserData();
  const hasOpenSession = localUserData?.user;

  useEffect(() => {
    if (hasOpenSession) {
      setupUserSession({
        user: localUserData.user,
        userRoleList: localUserData.userRoleList,
      });
    } else {
      // try to verify if has authentication with API
      fetchUserData.trigger(null, {
        onSuccess: (userData) => {
          setupUserSession({
            user: userData!.user,
            userRoleList: userData!.user_roles,
          });
        },
        onError: () => logoutInvalidUser(),
      });
    }
  }, []);

  // monitor notifications through route changes
  useEffect(() => {
    if (hasOpenSession) {
      // on route change, update document name according to unseen notification count
      const unseenNotificationText = `(${
        unseenNotificationCount > 99 ? '99+' : unseenNotificationCount
      })`;

      document.title = `${
        unseenNotificationCount ? unseenNotificationText : ''
      } Letrus`;
    }
  }, [pathname, notificationsData]);

  // reacts to user data changes
  useEffect(() => {
    const user = localUserData?.user;

    // sets up hubspot user after user fetch has finished
    if (hasOpenSession && import.meta.env.MODE === 'production' && user) {
      // If external API methods are already available, use them.
      if (window.HubSpotConversations) {
        setupUserHubspotConversation(user);
      } else {
        // Otherwise, callbacks can be added to the hsConversationsOnReady on the window object.
        // These callbacks will be called once the external API has been initialized.
        window.hsConversationsOnReady = [
          () => setupUserHubspotConversation(user),
        ];
      }
    }
  }, [hasOpenSession]);

  if (window.hsConversationsSettings) {
    window.hsConversationsSettings = {
      loadImmediately: false,
    };
  }

  function setupUserSession({ user, userRoleList }: UserSession) {
    // configures sentry user
    SentryUser({
      id: String(user?.id),
      email: user?.email,
      username: user?.username,
    });

    // Initialize Amplitude after authentication (once) and identify user
    if (user?.id && !isAmplitudeReady()) {
      initAmplitude();
      const schoolId = userRoleList?.[0]?.school;

      ensureAmplitudeUser(user.id, schoolId);
    }

    if (hasLocalFeatureFlags) {
      // get maintenance mode from session storage
      setIsUnderMaintenance(getFeatureFlag('maintenance_mode').isOn);
      // flags already locally available, we can stop checking auth now
      setIsCheckingAuth(false);
    } else {
      // updates feature flags if needed
      fetchFeatureFlags.trigger(
        {
          user,
          flagKeys,
          userRoles: userRoleList,
        },
        {
          onSuccess: (flags) => {
            setIsUnderMaintenance(
              !!findFeatureFlag(flags, 'maintenance_mode', 'on')
            );

            setIsCheckingAuth(false);
          },
          onError: () => {
            setIsCheckingAuth(false);
          },
        }
      );
    }
  }

  function logoutInvalidUser() {
    navigate('/login');
  }

  if (isCheckingAuth) {
    return <Loading show />;
  }

  if (isUnderMaintenance) {
    return <UnderMaintenance />;
  }

  if (pathname === '/aceite-termos' || pathname === '/primeiro-acesso') {
    return <Outlet />;
  }

  return (
    <AuthLayout>
      <Outlet />
      <LogoutModal />
    </AuthLayout>
  );
}

export default AuthRouteMiddleware;
